// ===========================================
// CONTACTS SERVICE - Production Ready
// ===========================================

import api from './api';
import { getApiBaseUrl } from '../lib/apiBase';

export interface Contact {
  id: string;
  email: string;
  firstName?: string;
  lastName?: string;
  phone?: string;
  company?: string;
  jobTitle?: string;
  leadStatus: string;
  leadScore: number;
  lifecycleStage: string;
  tags: string[];
  isSubscribed: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface ContactsListResponse {
  contacts: Contact[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

export interface ContactFilters {
  page?: number;
  limit?: number;
  search?: string;
  leadStatus?: string;
  lifecycleStage?: string;
  tags?: string[];
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
}

export interface CreateContactData {
  email: string;
  firstName?: string;
  lastName?: string;
  phone?: string;
  company?: string;
  jobTitle?: string;
  leadStatus?: string;
  tags?: string[];
}

export const contactsService = {
  async list(filters: ContactFilters = {}) {
    const params = new URLSearchParams();
    
    Object.entries(filters).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        if (Array.isArray(value)) {
          value.forEach(v => params.append(key, v));
        } else {
          params.append(key, String(value));
        }
      }
    });

    return api.get<ContactsListResponse>(`/api/contacts?${params.toString()}`);
  },

  async getById(id: string) {
    return api.get<Contact>(`/api/contacts/${id}`);
  },

  async create(data: CreateContactData) {
    return api.post<Contact>('/api/contacts', data);
  },

  async update(id: string, data: Partial<CreateContactData>) {
    return api.patch<Contact>(`/api/contacts/${id}`, data);
  },

  async delete(id: string) {
    return api.delete(`/api/contacts/${id}`);
  },

  async bulkDelete(ids: string[]) {
    return api.post('/api/contacts/bulk-delete', { ids });
  },

  async addTags(id: string, tags: string[]) {
    return api.post<Contact>(`/api/contacts/${id}/tags`, { tags });
  },

  async removeTags(id: string, tags: string[]) {
    return api.delete(`/api/contacts/${id}/tags?tags=${tags.join(',')}`);
  },

  async updateStatus(id: string, status: string) {
    return api.patch<Contact>(`/api/contacts/${id}/status`, { status });
  },

  async export(format: 'csv' | 'xlsx' = 'csv') {
    return api.get<Blob>(`/api/contacts/export?format=${format}`);
  },

  async import(file: File) {
    const formData = new FormData();
    formData.append('file', file);
    
    const response = await fetch(`${getApiBaseUrl()}/api/contacts/import`, {
      method: 'POST',
      body: formData,
      headers: {
        Authorization: `Bearer ${localStorage.getItem('tripnovac_access_token')}`,
      },
    });
    
    return response.json();
  },

  async getStats() {
    return api.get<{
      total: number;
      byStatus: Record<string, number>;
      byStage: Record<string, number>;
      recentlyAdded: number;
      subscribed: number;
    }>('/api/contacts/stats');
  },
};

export default contactsService;
